import React from 'react';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import {
  lifecycleStatusColor,
  lifecycleStatusGlyph,
  type LifecycleStageView,
} from '@/components/commercial-lifecycle/lifecycleStageView';

const { Text } = Typography;

export type CommercialLifecycleRailProps = {
  stages: LifecycleStageView[];
  activeStageKey?: string | null;
  onStageClick?: (stage: LifecycleStageView) => void;
  progressPercent?: number;
  title?: string;
  compact?: boolean;
};

/**
 * Clickable ECLE stage rail for Pursuit Workspace (EXTEND of timeline semantics).
 */
export const CommercialLifecycleRail: React.FC<CommercialLifecycleRailProps> = ({
  stages,
  activeStageKey,
  onStageClick,
  progressPercent,
  title = 'Commercial lifecycle',
  compact = false,
}) => {
  if (!stages.length) {
    return <Text type="secondary">No lifecycle stages</Text>;
  }

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <Text strong style={{ fontSize: compact ? 13 : 14 }}>
          {title}
        </Text>
        {progressPercent != null ? (
          <Tag>{Math.round(progressPercent)}%</Tag>
        ) : null}
      </Space>
      <div
        style={{
          display: 'flex',
          flexWrap: compact ? 'nowrap' : 'wrap',
          alignItems: 'center',
          gap: 4,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 4,
        }}
      >
        {stages.map((s, i) => {
          const focused = activeStageKey === s.stageKey;
          const color = lifecycleStatusColor(s.status);
          return (
            <React.Fragment key={s.stageKey}>
              {i > 0 ? (
                <Text type="secondary" style={{ margin: '0 4px', flexShrink: 0 }}>
                  →
                </Text>
              ) : null}
              <Tooltip
                title={[
                  s.label,
                  s.status.replace('_', ' '),
                  s.isOptional ? 'optional' : null,
                  s.requiresApproval ? 'requires approval' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <Button
                  type={focused ? 'primary' : 'default'}
                  size="small"
                  aria-label={`Lifecycle stage ${s.label}`}
                  onClick={() => onStageClick?.(s)}
                  style={{
                    flexShrink: 0,
                    minHeight: 36,
                    borderColor: s.status === 'blocked' ? '#ffccc7' : undefined,
                    color: focused ? undefined : color,
                  }}
                >
                  <span style={{ marginRight: 6 }}>{lifecycleStatusGlyph(s.status)}</span>
                  {s.label}
                </Button>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CommercialLifecycleRail;
